/**
 * Base Application Error
 * All custom errors inherit from this
 */
class ApplicationError extends Error {
    constructor(message, statusCode = 500, isOperational = true, context = {}) {
        super(message)
        this.name = this.constructor.name
        this.statusCode = statusCode
        this.isOperational = isOperational
        this.context = context
        this.timestamp = new Date().toISOString()
        Error.captureStackTrace(this, this.constructor)
    }

    toJSON() {
        return {
            type: this.name,
            message: this.message,
            statusCode: this.statusCode,
            isOperational: this.isOperational,
            context: this.context,
            timestamp: this.timestamp
        }
    }
}

/**
 * 400 - Validation Error
 * User input validation failures
 */
class ValidationError extends ApplicationError {
    constructor(message, context = {}) {
        super(message, 400, true, context)
    }
}

/**
 * 404 - Not Found Error
 * Requested resource doesn't exist
 */
class NotFoundError extends ApplicationError {
    constructor(message) {
        super(message, 404, true)
    }
}

/**
 * 409 - Duplicity Error
 * Resource already exists (unique constraint violation)
 */
class DuplicityError extends ApplicationError {
    constructor(message) {
        super(message, 409, true)
    }
}

/**
 * 401 - Credentials Error
 * Invalid login credentials
 */
class CredentialsError extends ApplicationError {
    constructor(message) {
        super(message, 401, true)
    }
}

/**
 * 500 - System Error
 * Internal server errors, database failures
 */
class SystemError extends ApplicationError {
    constructor(message) {
        super(message, 500, false)
    }
}

/**
 * 403 - Ownership Error
 * User doesn't own the resource
 */
class OwnershipError extends ApplicationError {
    constructor(message) {
        super(message, 403, true)
    }
}

/**
 * 401 - Authorization Error
 * Missing or invalid authentication
 */
class AuthorizationError extends ApplicationError {
    constructor(message) {
        super(message, 401, true)
    }
}

/**
 * 422 - Data Integrity Error
 * Business rule violations
 */
class DataIntegrityError extends ApplicationError {
    constructor(message) {
        super(message, 422, true)
    }
}

/**
 * 403 - Subscription Error
 * Subscription required or expired
 */
class SubscriptionError extends ApplicationError {
    constructor(message) {
        super(message, 403, true)
    }
}

/**
 * 400 - Content Error
 * Invalid content format or structure
 */
class ContentError extends ApplicationError {
    constructor(message, context = {}) {
        super(message, 400, true, context)
    }
}

/**
 * 429 - Rate Limit Error
 * Too many requests from client
 */
class RateLimitError extends ApplicationError {
    constructor(message = 'Too many requests', retryAfter = 60, context = {}) {
        super(message, 429, true, context)
        this.retryAfter = retryAfter
    }

    toJSON() {
        return {
            ...super.toJSON(),
            retryAfter: this.retryAfter
        }
    }
}

/**
 * 503 - Service Unavailable Error
 * Service temporarily unavailable (maintenance, overload, etc.)
 */
class ServiceUnavailableError extends ApplicationError {
    constructor(message = 'Service temporarily unavailable', retryAfter = 30, context = {}) {
        super(message, 503, false, context)
        this.retryAfter = retryAfter
    }

    toJSON() {
        return {
            ...super.toJSON(),
            retryAfter: this.retryAfter
        }
    }
}

/**
 * 503 - Worker Unreachable Error
 * The supermarket-workers control plane could not be reached or
 * responded with a non-recoverable transport error. Operational so
 * the caller can surface a retryable state to the user.
 */
class WorkerUnreachableError extends ApplicationError {
    constructor(message = 'Supermarket worker unavailable', context = {}) {
        super(message, 503, true, context)
    }
}

const errors = {
    ApplicationError,
    ValidationError,
    NotFoundError,
    DuplicityError,
    CredentialsError,
    SystemError,
    OwnershipError,
    AuthorizationError,
    DataIntegrityError,
    SubscriptionError,
    ContentError,
    RateLimitError,
    ServiceUnavailableError,
    WorkerUnreachableError
}

/**
 * Helper: Check if error is operational (expected) vs programmer error
 * @param {Error} error - Error to check
 * @returns {boolean} True if operational error
 */
export function isOperationalError(error) {
    if (error instanceof ApplicationError) {
        return error.isOperational
    }
    return false
}

/**
 * Helper: Get HTTP status code from error
 * @param {Error} error - Error to extract status from
 * @returns {number} HTTP status code
 */
export function getErrorStatusCode(error) {
    if (error instanceof ApplicationError) {
        return error.statusCode
    }
    return error.statusCode || error.status || 500
}

export default errors